const TEXT_INPUT_TYPES = new Set([
	'text',
	'url',
	'email',
	'tel',
	'search',
	'number',
	'password',
])

// Marks a hint we set ourselves, so it isn't mistaken for one a field set
// explicitly (e.g. the "go" hint on fields that submit the step).
const AUTO_HINT_ATTR = 'data-auto-enterkeyhint'

type TEnterKeyHint = 'next' | 'done'

const isTextInput = (el: Element): el is HTMLInputElement =>
	el instanceof HTMLInputElement && TEXT_INPUT_TYPES.has(el.type)

const isFocusable = (
	el: HTMLInputElement | HTMLTextAreaElement,
	container: HTMLElement,
) => {
	if (el.disabled || el.readOnly || el.tabIndex < 0) return false
	// Ark's color picker commits its value on Enter, so it's never a "next" target
	if (el.closest('[data-scope="color-picker"]')) return false
	// Collapsed AnimateHeight panels stay mounted but are aria-hidden
	const hiddenAncestor = el.closest('[aria-hidden="true"]')
	return !hiddenAncestor || !container.contains(hiddenAncestor)
}

export const isEnterNavigable = (
	el: EventTarget | null,
	container: HTMLElement,
): el is HTMLInputElement => {
	if (!(el instanceof Element) || !isTextInput(el)) return false
	// React bubbles events from portals (e.g. color picker popovers) up to here
	if (!container.contains(el)) return false
	if (!isFocusable(el, container)) return false
	if (el.hasAttribute('enterkeyhint') && !el.hasAttribute(AUTO_HINT_ATTR)) {
		return false
	}
	// Forms with a submit button rely on Enter to submit (e.g. adding a video)
	if (el.form?.querySelector('[type="submit"]')) return false
	return true
}

export const getNextField = (
	el: HTMLInputElement,
	container: HTMLElement,
): HTMLInputElement | HTMLTextAreaElement | undefined => {
	const fields = [...container.querySelectorAll('input, textarea')].filter(
		(field): field is HTMLInputElement | HTMLTextAreaElement =>
			(field instanceof HTMLTextAreaElement || isTextInput(field)) &&
			isFocusable(field, container),
	)
	const index = fields.indexOf(el)
	return index === -1 ? undefined : fields[index + 1]
}

export const resolveEnterKeyHint = (
	el: HTMLInputElement,
	container: HTMLElement,
): TEnterKeyHint => (getNextField(el, container) ? 'next' : 'done')

export const applyEnterKeyHint = (
	el: HTMLInputElement,
	container: HTMLElement,
) => {
	el.setAttribute(AUTO_HINT_ATTR, '')
	el.setAttribute('enterkeyhint', resolveEnterKeyHint(el, container))
}

export const advanceFromField = (
	el: HTMLInputElement,
	container: HTMLElement,
) => {
	const next = getNextField(el, container)
	if (!next) {
		el.blur()
		return
	}
	if (isEnterNavigable(next, container)) applyEnterKeyHint(next, container)
	next.focus()
}
